/**
 * Exam header component.
 *
 * Displays section title, chunk progress, timer, and finish button.
 *
 * @module ExamHeader
 */

import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Clock, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { CRITICAL_TIME_SECONDS, LOW_TIME_SECONDS } from "@/lib/constants";
import { playExamTimerWarningSound } from "@/utils/examTimerWarningSound";
import type { ExamModule } from "@/types/exam-simulation";
import { submitModuleButtonLabel } from "@/lib/simulationSubmitModuleLabels";
import { formatTimerAriaLabel } from "@/utils/timerAccessibility";

interface ExamHeaderProps {
  readonly sectionTitle: string;
  readonly currentChunk: number;
  readonly totalChunks: number;
  readonly timeRemaining: number;
  readonly onFinish: () => void;
  readonly disabled?: boolean;
  /**
   * `'default'`   – countdown with colors + auto-beep at < 5 min.
   * `'inactive'`  – timer hidden.
   * `'listening'` – countdown with colors, no auto-beep (caller owns the beep).
   * `'review'`    – review-period countdown (always red, no auto-beep).
   */
  readonly timerVariant?: "default" | "inactive" | "listening" | "review";
  readonly timerNote?: string | null;
  /**
   * Label shown before the chunk progress dots. Defaults to the `Passage`
   * translation so Reading renders identically when the caller does not pass
   * this prop. Listening/Writing pass `Part` / `Task` respectively.
   */
  readonly chunkLabel?: string;
  /** When set, the finish button uses a module-specific label (e.g. Submit Reading module). */
  readonly finishModule?: ExamModule;
}

export function ExamHeader({
  sectionTitle,
  currentChunk,
  totalChunks,
  timeRemaining,
  onFinish,
  disabled = false,
  timerVariant = "default",
  timerNote = null,
  chunkLabel,
  finishModule,
}: ExamHeaderProps) {
  const { t } = useTranslation();
  const hasPlayedWarningSound = useRef(false);
  const minutes = Math.floor(timeRemaining / 60);
  const seconds = timeRemaining % 60;
  const isInactive = timerVariant === "inactive";
  const isReview = timerVariant === "review";
  const showsCountdown =
    timerVariant === "default" || timerVariant === "listening";
  const isLowTime = showsCountdown && timeRemaining < LOW_TIME_SECONDS;
  const isCriticalTime =
    isReview || (showsCountdown && timeRemaining < CRITICAL_TIME_SECONDS);
  const warningLabel =
    isCriticalTime && !isReview
      ? t("simulation.active.timerWarning", {
          defaultValue: "Less than 5 mins left",
        })
      : undefined;
  const timerAriaLabel = formatTimerAriaLabel(timeRemaining, {
    prefix: t("simulation.active.timerAriaPrefix", {
      defaultValue: "Time remaining",
    }),
    minute: t("simulation.active.timerUnits.minute", {
      defaultValue: "minute",
    }),
    minutes: t("simulation.active.timerUnits.minutes", {
      defaultValue: "minutes",
    }),
    second: t("simulation.active.timerUnits.second", {
      defaultValue: "second",
    }),
    seconds: t("simulation.active.timerUnits.seconds", {
      defaultValue: "seconds",
    }),
    warning: warningLabel,
  });
  const TimerIcon = isCriticalTime && !isReview ? AlertTriangle : Clock;

  useEffect(() => {
    if (timeRemaining >= CRITICAL_TIME_SECONDS) {
      hasPlayedWarningSound.current = false;
      return;
    }
    if (
      timerVariant === "default" && // 'listening' variant manages its own beep
      timeRemaining > 0 &&
      timeRemaining < CRITICAL_TIME_SECONDS &&
      !hasPlayedWarningSound.current
    ) {
      hasPlayedWarningSound.current = true;
      playExamTimerWarningSound();
    }
  }, [timerVariant, timeRemaining]);

  const resolvedChunkLabel =
    chunkLabel ??
    t("simulation.active.chunkLabel.passage", { defaultValue: "Passage" });

  let finishButtonText: string;
  if (disabled) {
    finishButtonText = t("common.saving", { defaultValue: "Saving..." });
  } else if (finishModule) {
    finishButtonText = submitModuleButtonLabel(t, finishModule);
  } else {
    finishButtonText = t("simulation.active.finishSectionButton", {
      defaultValue: "Submit module",
    });
  }

  return (
    <header className="safe-area-top flex h-auto shrink-0 flex-col justify-between gap-2 border-b border-border bg-card px-3 pb-2 sm:h-16 sm:flex-row sm:items-center sm:gap-3 sm:px-6 sm:py-0">
      {/* Left: Section Title */}
      <div className="flex min-w-0 items-center gap-2 sm:gap-3">
        <div className="w-1.5 sm:w-2 h-6 sm:h-8 bg-primary rounded-full shrink-0" />
        <h1 className="font-ui font-bold text-foreground text-sm sm:text-lg tracking-tight truncate">
          {sectionTitle}
        </h1>
      </div>

      {/* Center: detailed progress is desktop-only; compact counters remain in the panels below lg. */}
      <div className="hidden items-center gap-2 overflow-x-auto py-0.5 lg:flex">
        <span className="font-ui hidden md:inline text-[10px] sm:text-sm text-muted-foreground font-medium mr-1 uppercase tracking-wider">
          {resolvedChunkLabel}
        </span>
        <div className="flex gap-1 sm:gap-1.5">
          {Array.from({ length: totalChunks }, (_, i) => {
            const getChunkClassName = () => {
              if (i < currentChunk) return "bg-primary";
              if (i === currentChunk)
                return "bg-primary shadow-sm shadow-primary/30";
              return "bg-border";
            };
            return (
              <div
                key={i}
                className={cn(
                  "w-5 sm:w-8 h-1.5 sm:h-2 rounded-full transition-all duration-300",
                  getChunkClassName(),
                )}
              />
            );
          })}
        </div>
        <span className="font-ui text-[10px] sm:text-sm text-muted-foreground ml-1 font-bold whitespace-nowrap">
          {currentChunk + 1} / {totalChunks}
        </span>
      </div>

      {/* Right: Timer & Finish Button (Listening hides timer until review via inactive variant) */}
      <div
        className={cn(
          "flex w-full min-w-0 items-center gap-2 sm:w-auto sm:gap-4",
          isInactive ? "justify-end" : "justify-between sm:justify-end",
        )}
      >
        {isInactive ? null : (
          <div
            role="timer"
            aria-live="polite"
            aria-atomic="true"
            aria-label={timerAriaLabel}
            className={cn(
              "flex min-w-0 items-center gap-1.5 rounded-lg border border-border bg-muted/80 px-2.5 py-1.5 shadow-sm shadow-border/30 sm:gap-2 sm:px-4 sm:py-2",
              isCriticalTime &&
                "bg-exam-timer-danger/10 border-exam-timer-danger/20",
              isLowTime &&
                !isCriticalTime &&
                "bg-exam-timer-warning/10 border-exam-timer-warning/20",
            )}
          >
            <TimerIcon
              className={cn(
                "w-4 h-4 sm:w-5 sm:h-5 shrink-0",
                (() => {
                  if (isCriticalTime) return "text-exam-timer-danger";
                  if (isLowTime) return "text-exam-timer-warning";
                  return "text-muted-foreground";
                })(),
              )}
            />
            <div className="flex flex-col leading-none sm:leading-tight">
              <span
                className={cn(
                  "font-ui font-bold text-sm sm:text-lg tabular-nums tracking-tighter sm:tracking-tight",
                  (() => {
                    if (isCriticalTime) return "text-exam-timer-danger";
                    if (isLowTime) return "text-exam-timer-warning";
                    return "text-foreground";
                  })(),
                )}
              >
                {String(minutes).padStart(2, "0")}:
                {String(seconds).padStart(2, "0")}
              </span>
              {(isCriticalTime && !isReview) || timerNote ? (
                <div className="flex flex-col">
                  {isCriticalTime && !isReview && (
                    <span className="font-ui text-[8px] sm:text-xs text-exam-timer-danger font-medium animate-pulse truncate max-w-[80px] sm:max-w-none">
                      {warningLabel}
                    </span>
                  )}
                  {timerNote && (
                    <span
                      className={cn(
                        "font-ui text-[8px] sm:text-xs truncate max-w-[80px] sm:max-w-none",
                        timerVariant === "review"
                          ? "text-exam-timer-danger font-medium"
                          : "text-muted-foreground",
                      )}
                    >
                      {timerNote}
                    </span>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        )}

        <Button
          onClick={onFinish}
          disabled={disabled}
          size="sm"
          className="h-11 shrink-0 bg-primary px-3 font-ui font-bold text-primary-foreground shadow-md shadow-primary/20 transition-transform hover:bg-primary/90 active:scale-95 sm:px-6"
        >
          <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
          <span className="font-ui text-xs sm:hidden">
            {t("simulation.active.submitShort", { defaultValue: "Submit" })}
          </span>
          <span className="font-ui hidden text-sm sm:inline">
            {finishButtonText}
          </span>
        </Button>
      </div>
    </header>
  );
}
